import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import styles from './StackedColumnChart.module.scss';
import StackedColumns from 'app/components/Charts/StackedColumns';
import SelectInput from 'app/components/Form/Inputs/SelectInput/SelectInput';
import { ReactComponent as CountryIcon } from 'app/assets/images/countryIcon.svg';
import { ReactComponent as PlatformIcon } from 'app/assets/images/platform.svg';
import DotsMenu from 'app/components/DotsMenu';
import { ReactComponent as ExpandIcon } from 'app/assets/images/expand.svg';
import { barLatestFiveHour, loadAllCountries } from 'app/Requests';
import { nFormatter } from 'app/utils/numbersFormater';
import Loader from '../Loader';
import { _t } from 'app/utils/i18n';
import { Link } from 'react-router-dom';
import CardTitle from '../CardTitle';

import TimeIntervalFilter from 'app/components/TimeIntervalFilter/TimeIntervalFilter';
import { changeStackedColumnsPlatformResponseSchema } from 'app/utils/helpers';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { fixed24HoursFormatter } from 'app/utils/dateTimeFormatter';

import { Grid } from '@material-ui/core';
import ServerError from '../ErrorBoundary/ServerError';
import NoData from '../NoData';
import { useAppSettings } from 'app/context/settings/appSettings';
interface Props {
  slug?: string;
  columnHeader?: string[];
  legendPosition?: string;
  isHorizontal?: boolean;
  showToolTip?: boolean;
  showLegend?: boolean;
  hidePlatformFilter?: boolean;
  hideTimeFilter?: boolean;
  hideCountryFilter?: boolean;
  data?: { values: { data: number[]; name: string }[]; xaxis: string[] };
  expandUrl?: string;
  redirectState?: any;
  customFilters?: any;
  compId: string;
  stackedColumnTitle?: string;
  showCustomizedLegend?: boolean;
  subCategoryTooltip?: boolean;
  stackType?: boolean;
  hideTimeIntervalDotsMenu?: boolean;
  unAnalyzed?: boolean;
  transformResponse?: boolean;
  allTimeInterval?: boolean;
  distributed?: boolean;
  customStyles?: any;
  fixed24Hours?: boolean;
  hideDotsMenu?: boolean;
  weekTimeInterval?: boolean;
  isSentiment?: boolean;
  withSecondaryTable?: boolean;
  weekTimeIntervalWithDay?: boolean;
  customTooltip?: any;
  trending_topics?: any;
  compeletLabelsText?: boolean;
  grid?: any;
  hideIfNoData?: boolean;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  allCountriesFilter?: boolean;
  withLegendCount?: boolean;
  noInitialDefaultCountries?: boolean;
  noLegendPercentage?: boolean;
  toolTipLimit?: number;
}
const StackedColumnChart: FunctionComponent<Props> = ({
  stackedColumnTitle,
  compId,
  slug,
  isHorizontal,
  showToolTip,
  showLegend,
  data,
  legendPosition,
  hidePlatformFilter,
  hideTimeFilter,
  hideCountryFilter,
  expandUrl,
  columnHeader,
  redirectState,
  customFilters,
  showCustomizedLegend,
  subCategoryTooltip,
  stackType,
  hideTimeIntervalDotsMenu,
  unAnalyzed,
  transformResponse,
  allTimeInterval,
  distributed,
  customStyles,
  fixed24Hours,
  hideDotsMenu,
  weekTimeInterval,
  isSentiment,
  withSecondaryTable,
  weekTimeIntervalWithDay,
  customTooltip,
  trending_topics,
  compeletLabelsText,
  grid,
  hideIfNoData,
  additionalComponents,
  isReport,
  allCountriesFilter,
  withLegendCount,
  noInitialDefaultCountries,
  noLegendPercentage,
  toolTipLimit,
}) => {
  if (customTooltip) showToolTip = true;
  const { theme } = useAppSettings();
  const [chartData, setChartData] = useState<any>(() => ({
    ...data,
    xaxis: fixed24Hours
      ? data?.xaxis?.map((_, index: number) =>
          fixed24HoursFormatter(index, false),
        )
      : data?.xaxis,
  }));
  const queryClient = useQueryClient();

  const countriesOptions = queryClient.getQueryData('getCountries') as any;
  const platforms = queryClient.getQueryData('getPlatforms') as any;

  const { data: AllCountries, isLoading: AllCountriesIsLoading } = useQuery(
    'AllCountries',
    loadAllCountries,
  );

  const defaultCountriesIds = countriesOptions
    ?.filter((option: any) => option?.default)
    ?.map((i: any) => i.value);
  const [filters, setFilters] = useState({
    ...customFilters,
    country_ids: hideCountryFilter
      ? null
      : noInitialDefaultCountries
        ? []
        : defaultCountriesIds,
  });

  const [isLoading, setIsLoading] = useState(slug ? true : false);
  const { mutateAsync: barLatestFiveHourMutation, isError } =
    useMutation(barLatestFiveHour);

  const loadData = async (moduleName: string, filtersOpts = {}) => {
    setIsLoading(true);
    const res = await barLatestFiveHourMutation({
      slug: moduleName,
      body: filtersOpts,
    });
    const result = {
      ...res,
      xaxis: fixed24Hours
        ? res?.xaxis?.map((_: any, index: number) =>
            fixed24HoursFormatter(index, false),
          )
        : res?.xaxis,
    };

    if (transformResponse) {
      const { customized_legend, values } = result;
      const platformTransformedValues =
        changeStackedColumnsPlatformResponseSchema(customized_legend, values);
      setChartData({
        ...result,
        values: platformTransformedValues,
      });
    } else setChartData(result);
    setIsLoading(false);
  };

  useEffect(() => {
    if (slug) {
      loadData(slug, filters);
    }
  }, []);

  const handleChangeFilters = (name: string) => (option: any) => {
    if (Array.isArray(option)) {
      const values = option.map((item: any) => Number(item.value));
      setFilters((prevFilters: any) => ({ ...prevFilters, [name]: values }));
      loadData(slug || '', { ...filters, [name]: values });
    } else {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [name]: option.value ? option.value : option,
      }));
      // setFilters({ ...filters, time_interval: option });
      loadData(slug || '', {
        ...filters,
        [name]: option.value ? option.value : option,
      });
    }
  };

  const tooltipContent = (
    colors: string[],
    series: any,
    seriesIndex: any,
    dataPointIndex: any,
    w: any,
    subCategoryTooltip?: any,
    tooltip?: any,
  ) => {
    if (subCategoryTooltip) {
      return `
          <div>
            <div class="title_name">
              <h6>${_t('influencers:total_subca')}</h6>
              <p>${tooltip?.[dataPointIndex]?.data?.length}</p>
            </div>
          </div>
          <div class="arrow_box">
            <div>
              <h6>${_t('influencers:subcategory')}</h6>
              <div>
                ${tooltip?.[dataPointIndex]?.data
                  ?.map((el: any, i: number) => {
                    return `
                      <div>
                        <div class="color_name">
                          <!-- <div
                            class="colorBox"
                            style="
                              border-color: ${colors[i]};
                              background-color: ${colors[i]};
                            "
                          ></div> -->
                          <p>${el?.subcategory_name}</p>
                        </div>
                      </div>
                    `;
                  })
                  .join(' ')}
              </div>
            </div>
            <div>
              <h6>${_t('influencers:influencers')}</h6>
              <div>
                ${tooltip?.[dataPointIndex]?.data
                  ?.map((el: any) => {
                    return `<div>${el?.influencer_count}</div>`;
                  })
                  .join(' ')}
              </div>
            </div>
          </div>
          `;
    } else if (trending_topics) {
      const names = w?.globals?.seriesNames.join(' ');
      const mentions = w?.globals?.initialSeries
        .map((item: any) => {
          if (item?.data?.[dataPointIndex] !== 0) {
            return `<div>${
              isSentiment
                ? nFormatter(item?.data?.[dataPointIndex]) + '%'
                : nFormatter(
                    item?.data?.[dataPointIndex]
                      ? item?.data?.[dataPointIndex]
                      : 0,
                  )
            }</div>`;
          }
        })
        .join(' ');
      return `<div class="trending_topics">
                 <div>
                   <h6>${columnHeader?.[0] ? columnHeader?.[0] : ''}</h6>
                   <div>${names}</div>
                 </div>
                 <div>
                   <h6>${columnHeader?.[1] ? columnHeader?.[1] : ''}</h6>
                   <div>${mentions}</div>
                 </div>
               </div>
         `;
    } else {
      const sortedGlobals = {
        ...w?.globals,
        initialSeries: w?.globals?.initialSeries
          ?.map((item: any, index: number) => ({
            ...item,
            index: index,
          }))
          ?.sort(
            (b: any, a: any) =>
              a?.data?.[dataPointIndex] - b?.data?.[dataPointIndex],
          ),
      };
      const names = sortedGlobals?.seriesNames
        .map((el: any, index: number) => {
          if (
            sortedGlobals?.initialSeries?.[index]?.data?.[dataPointIndex] !== 0
          ) {
            return `<div class="color_name">
            <div class="colorBox"
                  style="
                              border-color: ${
                                colors[
                                  sortedGlobals?.initialSeries?.[index]?.index %
                                    12
                                ]
                              };
                              background-color: ${
                                colors[
                                  sortedGlobals?.initialSeries?.[index]?.index %
                                    12
                                ]
                              };
                            "
                          ></div>
            ${
              sortedGlobals?.seriesNames?.[
                sortedGlobals?.initialSeries?.[index]?.index
              ]
            }</div>`;
          }
        })
        .slice(0, toolTipLimit || sortedGlobals?.seriesNames?.length)
        .join(' ');
      const mentions = sortedGlobals?.initialSeries
        .map((item: any) => {
          if (item?.data?.[dataPointIndex] !== 0) {
            return `<div>${
              isSentiment
                ? nFormatter(item?.data?.[dataPointIndex]) + '%'
                : nFormatter(
                    item?.data?.[dataPointIndex]
                      ? item?.data?.[dataPointIndex]
                      : 0,
                  )
            }</div>`;
          }
        })
        .slice(0, toolTipLimit || sortedGlobals?.initialSeries?.length)
        .join(' ');
      return `<div class="arrow_box">
                 <div>
                   <h6>${columnHeader?.[0] ? columnHeader?.[0] : ''}</h6>
                   <div>${names}</div>
                 </div>
                 <div>
                   <h6>${columnHeader?.[1] ? columnHeader?.[1] : ''}</h6>
                   <div>${mentions}</div>
                 </div>
               </div>
         `;
    }
  };
  if (AllCountriesIsLoading) return <Loader />;
  return (
    <ServerError requiredApiCalls={[isError]}>
      <Grid item key={compId} {...grid}>
        <div
          id={compId}
          className={`${styles.StackedColumnChart} ${slug}`}
          style={customStyles ? customStyles : undefined}
        >
          {stackedColumnTitle && (
            <CardTitle
              title={stackedColumnTitle}
              compId={compId}
              hideIcon={isReport}
            />
          )}
          <div
            className={`${styles.ChartBox}${
              withSecondaryTable ? styles.withSecondaryTable : ''
            }`}
            id={`${compId}-stackedColumn-content`}
            style={
              customStyles && {
                height: '100%',
              }
            }
            data-minheight="false"
          >
            {unAnalyzed ? (
              <p data-testid="unAnalyzedQuote" className={styles.unAnalyzed}>
                {_t('hashtags:cant_view_sentiment')}
              </p>
            ) : (
              <>
                <div
                  data-testid="AnalyzedQuote"
                  className={styles.ChartBox__toolBar}
                  id={`${compId}-stackedColumn-toolBar`}
                  style={{
                    display: hideDotsMenu ? 'none' : 'flex',
                  }}
                >
                  <div
                    className={styles.Filters}
                    id={`${compId}-stackedColumn-filter`}
                  >
                    {!hideCountryFilter && (
                      <div
                        data-testid="stackedColCountryFilter"
                        className={styles.Select}
                      >
                        {allCountriesFilter ? (
                          <SelectInput
                            icon={<CountryIcon />}
                            placeholder={_t('trends:Country')}
                            options={AllCountries}
                            isMulti
                            onChange={handleChangeFilters('country_ids')}
                            showDefaultValue
                            name={''}
                          />
                        ) : (
                          <SelectInput
                            icon={<CountryIcon />}
                            placeholder={_t('trends:Country')}
                            options={countriesOptions}
                            isMulti
                            onChange={handleChangeFilters('country_ids')}
                            showDefaultValue
                            name={''}
                          />
                        )}
                      </div>
                    )}
                    {!hidePlatformFilter && (
                      <div
                        data-testid="stackedColPlatformFilter"
                        className={styles.Select}
                      >
                        <SelectInput
                          icon={<PlatformIcon />}
                          placeholder={_t('trends:Platforms')}
                          options={platforms?.platformsOptions}
                          isMulti
                          onChange={handleChangeFilters('platform_ids')}
                          name={''}
                        />
                      </div>
                    )}
                    {!hideTimeFilter && (
                      <TimeIntervalFilter
                        data-testid="stackedColTimeFilter"
                        compId={compId}
                        handleChangeTime={(val) =>
                          handleChangeFilters('time_interval')(val)
                        }
                        weekTimeInterval={weekTimeInterval}
                        hideDotsMenu={hideTimeIntervalDotsMenu}
                        allTimeInterval={allTimeInterval}
                        weekTimeIntervalWithDay={weekTimeIntervalWithDay}
                        isReport={isReport}
                      />
                    )}
                  </div>
                  {showCustomizedLegend && (
                    <div
                      className={styles.specityChartHolder}
                      id={`${compId}-stackedColumn-legend`}
                    >
                      {chartData?.customized_legend?.map((val: any, i: any) => {
                        return (
                          <div key={i} className={styles.specifyChart}>
                            <div
                              className={styles.colorBox}
                              style={{
                                borderColor: theme.colors.chartsColors[i],
                                backgroundColor: theme.colors.chartsColors[i],
                              }}
                            ></div>
                            <p>{val}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className={styles.Actions}>
                    {expandUrl && (
                      <div className={styles.ActionBtn}>
                        <Link
                          to={{ pathname: expandUrl, state: redirectState }}
                          id={`${compId}-stackedColumn-expandIcon`}
                        >
                          <ExpandIcon />
                        </Link>
                      </div>
                    )}
                    <div className={styles.ActionBtn}>
                      {!isReport && <DotsMenu id={compId} />}
                    </div>
                  </div>
                </div>
                {isLoading && !isError ? (
                  <Loader />
                ) : isError ? (
                  <NoData />
                ) : (
                  <StackedColumns
                    stackedSeries={chartData?.values}
                    isHorizontal={isHorizontal ? true : false}
                    categories={chartData?.xaxis}
                    // isTime={true}
                    noLegendPercentage={noLegendPercentage}
                    legendPosition={legendPosition}
                    withLegendCount={withLegendCount}
                    showToolTip={showToolTip}
                    showLegend={showLegend ? true : false}
                    showToolbar={false}
                    distributed={distributed}
                    customTooltip={
                      customTooltip && trending_topics
                        ? ({ series, seriesIndex, dataPointIndex, w }: any) => {
                            w.globals.seriesNames = customTooltip?.seriesNames;
                            return tooltipContent(
                              theme.colors.chartsColors,
                              series,
                              seriesIndex,
                              dataPointIndex,
                              w,
                            );
                          }
                        : ({ series, seriesIndex, dataPointIndex, w }: any) => {
                            return tooltipContent(
                              theme.colors.chartsColors,
                              series,
                              seriesIndex,
                              dataPointIndex,
                              w,
                              subCategoryTooltip,
                              chartData?.tooltip,
                            );
                          }
                    }
                    compId={`${compId}-stackedColumn`}
                    stackType={stackType}
                    compeletLabelsText={compeletLabelsText}
                  />
                )}
              </>
            )}
          </div>
          {additionalComponents}
        </div>
      </Grid>
    </ServerError>
  );
};
export default StackedColumnChart;
